'use client'

/**
 * IdleGuard — auto-logout after a configurable period of inactivity.
 *
 * HIPAA Security Rule §164.312(a)(2)(iii) requires automatic session
 * termination after inactivity. Default: 30 minutes. Override via
 * NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES env var.
 *
 * Mechanism:
 *   - Tracks mouse/keyboard/touch/scroll activity on the document.
 *   - 2 minutes before timeout: raises a persistent warning toast
 *     (lib/guard-toasts.tsx) with a live countdown, updated in place each
 *     second, and a "Stay signed in" action. Its × closes the notice only —
 *     the countdown and the sign-out carry on unless the user clicks the action.
 *   - At timeout: calls signOut() → redirects to /login?r=idle, where the login
 *     page explains why (param kept WAF-safe — AFD SQLi ruleset trips on the literal word
 *     "session" and the `_timeout` pattern, blocking the recovery URL).
 *   - Cross-tab: BroadcastChannel broadcasts activity so all open tabs
 *     share one idle clock. Activity in any tab resets all tabs.
 *
 * Does NOT auto-logout during active AI generation — the chat handler
 * dispatches a 'crucible:activity' document event on each streamed chunk
 * which resets the idle timer without requiring user input.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { useSession, signOut } from 'next-auth/react'
import { GUARD_TOAST_ID, hideGuardToast, showIdleToast } from '@/lib/guard-toasts'

const TIMEOUT_MS =
  (Number(process.env.NEXT_PUBLIC_IDLE_TIMEOUT_MINUTES) || 30) * 60 * 1000
// WARNING_BEFORE_MS scales with the timeout in test/dev, capped at 2 min in prod.
const WARNING_BEFORE_MS = Math.min(2 * 60 * 1000, TIMEOUT_MS * 0.4)
const CHANNEL_NAME = 'crucible-idle'

// Throttle activity resets — don't re-broadcast on every pixel of mouse movement.
const THROTTLE_MS = Math.min(10_000, TIMEOUT_MS * 0.1)

export function IdleGuard() {
  const { status } = useSession()
  const [showWarning, setShowWarning] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_BEFORE_MS / 1000))

  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastBroadcastRef = useRef<number>(0)
  const channelRef = useRef<BroadcastChannel | null>(null)
  // True once the user has closed the toast with its ×. The countdown keeps
  // running, but we stop re-raising the toast every second until the next
  // warning cycle begins.
  const toastClosedRef = useRef(false)

  const clearTimers = useCallback(() => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current)
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    if (countdownRef.current) clearInterval(countdownRef.current)
  }, [])

  // Arming is pure scheduling — it touches timers only. Every setState it
  // leads to happens inside a timer callback, never synchronously, which is
  // what lets the mount effect below call it directly.
  const armTimers = useCallback(() => {
    if (status !== 'authenticated') return
    clearTimers()

    warningTimerRef.current = setTimeout(() => {
      toastClosedRef.current = false
      setShowWarning(true)
      let s = Math.floor(WARNING_BEFORE_MS / 1000)
      setSecondsLeft(s)
      countdownRef.current = setInterval(() => {
        s -= 1
        setSecondsLeft(s)
      }, 1000)
    }, TIMEOUT_MS - WARNING_BEFORE_MS)

    logoutTimerRef.current = setTimeout(() => {
      signOut({ callbackUrl: '/login?r=idle' })
    }, TIMEOUT_MS)
  }, [status, clearTimers])

  const resetIdle = useCallback(() => {
    if (status !== 'authenticated') return
    // Do NOT hide the warning here — mouse movement resets the timer silently.
    // Only explicit clicks ("Stay signed in") dismiss the toast so it stays
    // visible long enough for the user to notice and interact with it. The
    // countdown *number* is rewound, so a warning that is already on screen
    // restarts from the full 2:00 rather than freezing at its last tick.
    setSecondsLeft(Math.floor(WARNING_BEFORE_MS / 1000))
    armTimers()
  }, [status, armTimers])

  const handleActivity = useCallback(() => {
    const now = Date.now()
    resetIdle()
    // Throttle broadcasts so mousemove doesn't flood the channel
    if (now - lastBroadcastRef.current > THROTTLE_MS) {
      lastBroadcastRef.current = now
      channelRef.current?.postMessage({ type: 'activity' })
    }
  }, [resetIdle])

  useEffect(() => {
    if (status !== 'authenticated') return

    channelRef.current = typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel(CHANNEL_NAME)
      : null

    channelRef.current?.addEventListener('message', (e) => {
      if (e.data?.type === 'activity') resetIdle()
    })

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll']
    events.forEach(ev => document.addEventListener(ev, handleActivity, { passive: true }))
    // Also reset on AI generation activity (chat handler fires this custom event)
    document.addEventListener('crucible:activity', handleActivity)

    // Arming the inactivity timers is the point of this effect — subscription
    // setup alongside the listeners above. It schedules the warning and
    // logout timeouts and sets no state of its own; `secondsLeft` is already
    // at its full value here and is rewound by the warning timer when it
    // fires, so there is nothing for the mount pass to correct.
    armTimers()

    return () => {
      clearTimers()
      events.forEach(ev => document.removeEventListener(ev, handleActivity))
      document.removeEventListener('crucible:activity', handleActivity)
      channelRef.current?.close()
    }
  }, [status, handleActivity, resetIdle, armTimers, clearTimers])

  // Raise the toast when the warning opens and tick it in place each second
  // (same id -- it never stacks). Telemetry (`notification_shown {surface:
  // 'idle-guard', kind:'warning', reason:'inactivity_pending'}`) fires once, when
  // the toast first appears, from the toast wrapper.
  const stay = useRef(() => {})
  useEffect(() => {
    stay.current = () => {
      handleActivity()
      setShowWarning(false)
    }
  })

  useEffect(() => {
    if (!showWarning) {
      toastClosedRef.current = false
      hideGuardToast(GUARD_TOAST_ID.idle)
      return
    }
    if (toastClosedRef.current) return
    showIdleToast({
      secondsLeft,
      onStay: () => stay.current(),
      // × dismisses the notice only; the logout timer is untouched.
      onClose: () => {
        toastClosedRef.current = true
      },
    })
  }, [showWarning, secondsLeft])

  // Unmount only -- the effect above updates the toast in place.
  useEffect(() => () => hideGuardToast(GUARD_TOAST_ID.idle), [])

  return null
}
